import { AuditEntityType, InvoiceDto, InvoiceRowDto, PurchaseDto } from '@sr/dto';
import { useContext, useRef } from 'react';
import { TextField } from 'formik-mui';
import { Field, Form, Formik, FormikHelpers, FormikProps } from 'formik';
import { Box, Button, Card, CardContent, MenuItem, Stack, Typography } from '@mui/material';
import { RelatedPayorders } from 'entities/invoice-report/related-payorders';
import { useRelatedPayOrders, useRelatedPurchasePayOrders } from 'entities/invoice-report/related-payorders.hook';
import { createPayOrderFromPurchase } from 'shared/api/purchases';
import { useSnack } from 'shared/ui/snack';
import { PurchseDetailPageContext } from 'pages/purchases/purchase-details-page';
import { useAppDispatch } from 'shared/store';
import { invalidateEntityAudit } from 'entities/audit/utils';

type Props = {
  invoice: InvoiceDto;
  invoiceRow: InvoiceRowDto;
  purchase: PurchaseDto;
  disabled?: boolean;
};

export type FormData = {
  amountOption: number;
  amount: number;
};

type ValidationData = Partial<{ [x in keyof FormData]: string }>;

const initialValues: FormData = {
  amountOption: 0,
  amount: 0,
};

const NO_PAY_ORDER_PAYMENT_OPTIONS = new Map<string, number>([
  ['', 0],
  ['100%', 1],
  ['50%', 0.5],
  ['30%', 0.3],
]);

const DEFAULT_PAYMENT_OPTIONS = new Map<string, number>([
  ['', 0],
  ['Остаток', 1],
]);

export const PurchasePayOrderForm = (props: Props) => {
  const { payOrders } = useRelatedPurchasePayOrders(props.invoice.id,props.purchase.id);
  const { showSuccess, showError } = useSnack();
  const dispatch = useAppDispatch();
  const submitPromise = useRef<Promise<void> | null>(null);

  const context = useContext(PurchseDetailPageContext);

  const handleSubmit = (values: FormData, { resetForm }: FormikHelpers<FormData>) => {
    if (props.disabled) return Promise.resolve();
    if (submitPromise.current) return submitPromise.current;
    submitPromise.current = createPayOrderFromPurchase(props.purchase.id, { amount: values.amount })
      .then((payOrder) => {
        resetForm();
        showSuccess('Платежное поручение успешно создано');
        invalidateEntityAudit(dispatch, AuditEntityType.PURCHASE, props.purchase.id);
        invalidateEntityAudit(dispatch, AuditEntityType.PAYORDER, payOrder?.id);
        payOrders?.reload?.();
        context?.setReload((reload) => !reload);
      })
      .catch((e) => showError(`Ошибка создания платежного поручения: ${e}`))
      .finally(() => {
        submitPromise.current = null;
      });

    return submitPromise.current;
  };

  const validate = (values: FormData): ValidationData => {
    let validationResult: ValidationData = {};

    if (!values.amount) {
      validationResult.amount = 'Не указана сумма платежки';
    }

    const maxTotalAmount = Math.max(0, props.invoice.totals.priceTotal - props.invoice.totals.partialPaidAmount);

    if (values.amount > maxTotalAmount) {
      validationResult.amount = `Остаток по счёту: ${maxTotalAmount}`;
    }

    return validationResult;
  };

  const handleAmountOptionChange = (event: any, setValues: FormikProps<FormData>['setValues']) => {
    const value = Number((event.target as HTMLSelectElement).value);
    setValues({
      amountOption: value,
      amount: value * (props.purchase.supplierInvoiceRow.priceDiscount - props.invoice.totals.partialPaidAmount),
    });
  };

  return (
    <>
      <Box marginTop={2}>
        <Typography variant="h6" gutterBottom>
          Платежные поручения
        </Typography>
        <RelatedPayorders payOrders={payOrders} referenceInvoiceId={props.invoice.id} />
      </Box>
      <Card sx={{ my: 3, maxWidth: 400 }}>
        <CardContent>
          <Formik initialValues={initialValues} onSubmit={handleSubmit} validate={validate}>
            {({ isSubmitting, values, setValues }: FormikProps<FormData>) => (
              <Form aria-label="Создание платежного поручения">
                <Stack direction="column" spacing={2}>
                  <Field
                    onChange={(e: any) => handleAmountOptionChange(e, setValues)}
                    label="Процент оплаты"
                    component={TextField}
                    select
                    name="amountOption"
                    disabled={props.disabled || isSubmitting}
                  >
                    {Array.from((payOrders.items.length ? DEFAULT_PAYMENT_OPTIONS : NO_PAY_ORDER_PAYMENT_OPTIONS).entries()).map(([key, value]) => (
                      <MenuItem sx={{ height: 36 }} key={value} value={value}>
                        {key}
                      </MenuItem>
                    ))}
                  </Field>

                  <Field required disabled={values.amountOption !== 0 || props.disabled || isSubmitting} label="Сумма оплаты" component={TextField} name="amount" />
                  <Button type="submit" variant="contained" sx={{ ml: 'auto', mr: 1 }} disabled={props.disabled || isSubmitting}>
                    {isSubmitting ? 'Сохранение…' : 'Сохранить'}
                  </Button>
                </Stack>
              </Form>
            )}
          </Formik>
        </CardContent>
      </Card>
    </>
  );
};
